import { useState, useEffect } from 'react';
import { ArrowUp, Info } from 'lucide-react';
import Confetti from 'react-confetti';
import { Toaster, toast } from 'react-hot-toast';
import { Modal, ModalHeader, ModalContent, ModalBasicLayout } from '@vibe/core/next';
import { Button, Box, Flex } from '@vibe/core';
import featureExplanations from './featureExplanations';

export default function VotingList() {
  const [items, setItems] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [votedItems, setVotedItems] = useState(new Set());
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const storedVotes = JSON.parse(localStorage.getItem('votedItems')) || [];
    setVotedItems(new Set(storedVotes));
    fetchFeaturesList();
  }, []);

  const fetchFeaturesList = async () => {
    try {
      const response = await fetch('api/featuresList');
      const data = await response.json();

      sortItemsByStatusAndVotes(data);
    } catch (error) {
      console.error('Error fetching features list:', error);
    }
  };

  const sortItemsByStatusAndVotes = (items) => {
    const sortedItems = [...items].sort((a, b) => {
      // Sort by status: pending first, then implemented (optional, but keep it consistent)
      // Or just sort by votes as before, but maybe implemented at the bottom?
      // The user didn't specify sorting, but usually implemented go down.
      if (a.status === 'pending' && b.status === 'implemented') return -1;
      if (a.status === 'implemented' && b.status === 'pending') return 1;
      return b.votes - a.votes;
    });
    setItems(sortedItems);
  };

  const handleItemClick = (item) => {
    if (item.status === 'implemented') {
      const explanation = featureExplanations[item._id];
      if (explanation) {
        setSelectedFeature(item);
        setIsModalOpen(true);
      } else {
        toast("פיצ'ר זה כבר קיים באתר! תשתמשו ותהנו!", {
          duration: 3000,
          icon: '🎉',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        });
      }
      return;
    }
    handleVote(item._id);
  };

  const handleVote = (id) => {
    if (votedItems.has(id)) {
      toast("כבר הצבעת עבור פיצ'ר זה!", { duration: 1500 });
      return;
    }

    const updatedItems = items.map((item) =>
      item._id === id ? { ...item, votes: item.votes + 1 } : item
    );
    sortItemsByStatusAndVotes(updatedItems);
    sendVoteToServer(id);
    const newVotedItems = new Set(votedItems).add(id);
    setVotedItems(newVotedItems);
    localStorage.setItem('votedItems', JSON.stringify([...newVotedItems]));
  };

  const sendVoteToServer = async (id) => {
    setShowConfetti(true);
    toast('תודה על ההצבעה!', { duration: 1500 });
    setTimeout(() => setShowConfetti(false), 3000);
    try {
      await fetch('api/featureVote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });
    } catch (error) {
      console.error('Error sending vote to server:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeature(null);
  };

  return items.length > 0 ? (
    <>
      <Toaster />
      {showConfetti && <Confetti />}

      <Modal
        id="feature-details-modal"
        show={isModalOpen}
        onClose={handleCloseModal}
        width="450px"
        title={selectedFeature?.title}
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '100vw',
          maxHeight: '100vh',
          overflow: 'auto',
          fontFamily: 'inherit',
          zIndex: 10000,
        }}
      >
        <ModalBasicLayout>
          <ModalHeader title={selectedFeature?.title} />
          <ModalContent>
            <Box padding="medium" style={{ textAlign: 'right', direction: 'rtl' }}>
              <div className="text-lg text-gray-700 leading-relaxed">
                {selectedFeature && featureExplanations[selectedFeature._id]}
              </div>
              <Flex justify="center" style={{ marginTop: '24px' }}>
                <Button
                  onClick={handleCloseModal}
                  style={{ backgroundColor: '#FFDAB9', color: 'black', fontWeight: 'bold' }}
                >
                  הבנתי, תודה!
                </Button>
              </Flex>
            </Box>
          </ModalContent>
        </ModalBasicLayout>
      </Modal>

      <div className="max-w-lg mx-auto mt-40 px-4 pb-12">
        <h2 className="text-2xl font-bold mb-4 text-center">
          הצביעו והשפיעו! מה הפיצ'ר הבא שחסר באתר?
        </h2>
        <div className="mt-8">
          <ol className="space-y-3">
            {items.map((item, index) => {
              const isImplemented = item.status === 'implemented';
              return (
                <li
                  key={item._id}
                  className={`flex items-center justify-between p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border ${
                    isImplemented
                      ? 'bg-green-50 border-green-200 opacity-90'
                      : 'bg-white border-transparent text-gray-800'
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flex items-center overflow-hidden">
                    <span
                      className={`text-lg font-semibold ml-3 shrink-0 ${isImplemented ? 'text-green-700' : 'text-gray-400'}`}
                    >
                      {index + 1}.
                    </span>
                    <span
                      className={`truncate font-medium ${isImplemented ? 'text-green-800' : 'text-gray-800'}`}
                    >
                      {item.title}
                    </span>
                    {isImplemented && (
                      <span className="mr-2 px-2 py-0.5 bg-green-200 text-green-800 text-xs font-bold rounded-full whitespace-nowrap">
                        בוצע!
                      </span>
                    )}
                  </div>
                  <div className="flex items-center ml-2 shrink-0">
                    <span
                      className={`ml-2 font-bold ${isImplemented ? 'text-green-700' : 'text-gray-800'}`}
                    >
                      {item.votes}
                    </span>
                    {isImplemented ? (
                      <Info className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowUp className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </>
  ) : null;
}
